import { ref, watch } from 'vue';

export const SALES_LISTS = Object.freeze([
  { id: 'rascunhos', key: 'DRAFTS' },
  { id: 'revisoes', key: 'REVIEW' },
  { id: 'entregues', key: 'DELIVERED' },
  { id: 'arquivados', key: 'TRASH' },
  { id: 'finalizados', key: 'FINISHED' },
]);

export const defaultSalesFilters = () => ({
  q: '',
  address_street: '',
  address_number: '',
  manager_user_id: [],
  responsible_user_id: [],
  post_sale_status: [],
  draft_status: [],
  payment_method: [],
  receipt_type: [],
  review_section: [],
  sort: '',
});

const routeValue = value => (Array.isArray(value) ? value[0] : value);

const arrayQuery = value => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const filtersFromQuery = query => ({
  q: routeValue(query.q) || '',
  address_street: routeValue(query.address_street) || '',
  address_number: routeValue(query.address_number) || '',
  manager_user_id: arrayQuery(query.manager_user_id),
  responsible_user_id: arrayQuery(query.responsible_user_id),
  post_sale_status: arrayQuery(query.post_sale_status),
  draft_status: arrayQuery(query.draft_status),
  payment_method: arrayQuery(query.payment_method),
  receipt_type: arrayQuery(query.receipt_type),
  review_section: arrayQuery(query.review_section),
  sort: routeValue(query.sort) || '',
});

const compactQuery = value =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) =>
        item !== '' &&
        item !== undefined &&
        (!Array.isArray(item) || item.length)
    )
  );

export const salesIndexLocation = ({
  accountId,
  query = {},
  fallbackList = 'rascunhos',
}) => ({
  name: 'operacao_imobiliaria_vendas',
  params: { accountId },
  query: {
    ...query,
    section: 'vendas',
    list: routeValue(query.list) || fallbackList,
  },
});

export function useSalesRouteState({ route, router }) {
  const activeSection = ref(routeValue(route.query.section) || 'painel');
  const activeList = ref(routeValue(route.query.list) || 'rascunhos');
  const filters = ref({
    ...defaultSalesFilters(),
    ...filtersFromQuery(route.query),
  });
  const page = ref(Number(routeValue(route.query.page) || 1));

  const syncIndex = () =>
    router.replace({
      name: 'operacao_imobiliaria_vendas',
      params: { accountId: route.params.accountId },
      query: compactQuery({
        section: activeSection.value,
        list: activeList.value,
        page: page.value,
        ...filters.value,
      }),
    });

  const openDetail = (id, detailTab = '') =>
    router.push({
      name: 'operacao_imobiliaria_vendas_detalhes',
      params: { accountId: route.params.accountId, salesTransactionId: id },
      query: compactQuery({
        section: activeSection.value,
        list: activeList.value,
        page: page.value,
        detail_tab: detailTab,
        ...filters.value,
      }),
    });

  const contextualRoute = (name, id) =>
    router.push({
      name,
      params: { accountId: route.params.accountId, salesTransactionId: id },
      query: compactQuery({
        section: activeSection.value,
        list: activeList.value,
        page: page.value,
        ...filters.value,
      }),
    });

  const openRegistration = id =>
    contextualRoute('operacao_imobiliaria_vendas_cadastro', id);

  const openDelivery = id =>
    contextualRoute('operacao_imobiliaria_vendas_entregar', id);

  const openSignatures = id =>
    contextualRoute('operacao_imobiliaria_vendas_entregar_checkout', id);

  const openReview = id =>
    contextualRoute('operacao_imobiliaria_vendas_revisar', id);

  const closeDetail = () =>
    router.push({
      name: 'operacao_imobiliaria_vendas',
      params: { accountId: route.params.accountId },
      query: compactQuery({
        section: activeSection.value,
        list: activeList.value,
        page: page.value,
        ...filters.value,
      }),
    });

  watch(
    () => route.fullPath,
    () => {
      activeSection.value = routeValue(route.query.section) || 'painel';
      activeList.value = routeValue(route.query.list) || 'rascunhos';
      filters.value = {
        ...defaultSalesFilters(),
        ...filtersFromQuery(route.query),
      };
      page.value = Number(routeValue(route.query.page) || 1);
    }
  );

  return {
    activeList,
    activeSection,
    closeDetail,
    filters,
    openDetail,
    openDelivery,
    openRegistration,
    openSignatures,
    openReview,
    page,
    syncIndex,
  };
}
